import type { DailyTaskRewardDto } from "../../api/dto/daily_tasks.dto.js";
import { rewardAmountText } from "../../common/reward_format.js";
import { ImgID } from "../../image_loader/img_id.js";
import { ImagesHolder } from "../../image_loader/images_holder.js";
import { Game } from "../../game.js";
import { dailyTaskRewardIcon } from "./reward_icon.js";
import { ColorScheme } from "./color_scheme.js";
import { TextStyleScheme } from "./text_style_scheme.js";
import { UIElement } from "./ui_element.js";

export class UIWheelSegments extends UIElement
{
  public static readonly DESIGN_SIZE = 860;
  public static readonly SEGMENT_COUNT = 12;
  public static readonly SEGMENT_ANGLE = Math.PI * 2 / UIWheelSegments.SEGMENT_COUNT;
  public static readonly COIN_R = 285;
  public static readonly COIN_SIZE = 112;
  public static readonly TEXT_OUTLINE = "#2A1F3D";

  private cache: HTMLCanvasElement | null = null;

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, size: number, private rewards: DailyTaskRewardDto[] )
  {
    super( ctx, x, y, size, size, false, false );
    this.setClickable( false );
    this.enableRotation();
  }

  public static rewardIcon( reward: DailyTaskRewardDto ): ImgID | null
  {
    if ( reward.type === "coin" )
      return ImgID.UI_ShopBlockImg;

    return dailyTaskRewardIcon( reward.type ) ?? null;
  }

  public static rewardLabel( reward: DailyTaskRewardDto ): string
  {
    return reward.type === "coin" ? `${reward.amount}` : rewardAmountText( reward.type, reward.amount );
  }

  public static fitSize( image: ImageBitmap, box: number ): { w: number; h: number }
  {
    const scale = box / Math.max( image.width, image.height, 1 );
    return { w: image.width * scale, h: image.height * scale };
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();

    const size = this.rect.getW();
    const cache = this.ensureCache( size );
    if ( cache )
      this.ctx.drawImage( cache, -size / 2, -size / 2, size, size );
    else
      this.drawContent( this.ctx, size );

    this.finishRender();
  }

  private ensureCache( size: number ): HTMLCanvasElement | null
  {
    if ( this.cache )
      return this.cache;

    if ( !this.imagesReady() )
      return null;

    const px = Math.ceil( size * Game.getDpr() );
    const canvas = document.createElement( "canvas" );
    canvas.width = px;
    canvas.height = px;

    const ctx = canvas.getContext( "2d" );
    if ( !ctx )
      return null;

    const scale = px / size;
    ctx.setTransform( scale, 0, 0, scale, px / 2, px / 2 );
    this.drawContent( ctx, size );
    this.cache = canvas;
    return canvas;
  }

  private imagesReady(): boolean
  {
    if ( !ImagesHolder.isReady( ImgID.UI_WheelOfFortuneContent ) )
      return false;

    return this.rewards.every( reward =>
    {
      const icon_id = UIWheelSegments.rewardIcon( reward );
      return icon_id === null || ImagesHolder.isReady( icon_id );
    } );
  }

  private drawContent( ctx: CanvasRenderingContext2D, size: number ): void
  {
    const s = size / UIWheelSegments.DESIGN_SIZE;

    ctx.save();
    ctx.scale( s, s );

    const content = ImagesHolder.getImage( ImgID.UI_WheelOfFortuneContent );
    const content_size = 762;
    const content_x = -content_size / 2;
    const content_y = -content_size / 2;
    ctx.drawImage( content, content_x, content_y, content_size, content_size );

    for ( let i = 0; i < UIWheelSegments.SEGMENT_COUNT; i++ )
      this.drawSegmentContent( ctx, i );

    ctx.restore();
  }

  private drawSegmentContent( ctx: CanvasRenderingContext2D, i: number ): void
  {
    const { COIN_R, COIN_SIZE } = UIWheelSegments;
    const text_r = 222;

    ctx.save();
    ctx.rotate( ( i + 0.5 ) * UIWheelSegments.SEGMENT_ANGLE );

    const reward = this.rewards[i];
    const icon_id = UIWheelSegments.rewardIcon( reward );

    if ( icon_id !== null )
    {
      const icon = ImagesHolder.getImage( icon_id );
      const { w, h } = UIWheelSegments.fitSize( icon, COIN_SIZE );
      ctx.drawImage( icon, -w / 2, -COIN_R - h / 2, w, h );
    }

    ctx.font = `900 34px ${TextStyleScheme.rubik}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.lineJoin = "round";
    ctx.lineWidth = 8;
    ctx.strokeStyle = UIWheelSegments.TEXT_OUTLINE;

    const text = UIWheelSegments.rewardLabel( reward );
    ctx.strokeText( text, 0, -text_r );
    ctx.fillStyle = ColorScheme.white;
    ctx.fillText( text, 0, -text_r );

    ctx.restore();
  }

  public name(): string
  {
    return "UIWheelSegments";
  }
}
